import { useCallback } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useAccount } from 'frontastic';
import { AddressFormData } from './address-form';

const usePropsToAddressType = () => {
  const { getCountry } = useLocale();
  const { formatMessage } = useFormat({ name: 'checkout' });

  const { addBillingAddress, addShippingAddress, setDefaultBillingAddress, setDefaultShippingAddress, updateAddress } =
    useAccount();

  const getAddressType = useCallback((address: AddressFormData) => {
    if (address?.addressType) return address?.addressType;
    return address?.isBillingAddress ? 'billing' : 'shipping';
  }, []);

  const mapPropsToAddress = useCallback(
    (address: AddressFormData) => {
      const addressType = getAddressType(address);

      const typeBasedProps = {
        billing: {
          checked: address?.isDefaultBillingAddress,
          label: formatMessage({ id: 'billing', defaultMessage: 'Billing' }).toLowerCase(),
          addressType: addressType,
          setAsDefault: async () => {
            await setDefaultBillingAddress(address?.addressId);
          },
          addAddress: async () => {
            const country = getCountry();
            await addBillingAddress({ ...address, country, isDefaultBillingAddress: address?.isDefaultAddress });
          },
          updateAddress: async () => {
            await updateAddress({ ...address, isDefaultBillingAddress: address?.isDefaultAddress });
          },
        },
        shipping: {
          checked: address?.isDefaultShippingAddress,
          label: formatMessage({ id: 'shipping', defaultMessage: 'Shipping' }).toLowerCase(),
          addressType: addressType,
          setAsDefault: async () => {
            await setDefaultShippingAddress(address?.addressId);
          },
          addAddress: async () => {
            const country = getCountry();
            await addShippingAddress({ ...address, country, isDefaultShippingAddress: address?.isDefaultAddress });
          },
          updateAddress: async () => {
            await updateAddress({ ...address, isDefaultShippingAddress: address?.isDefaultAddress });
          },
        },
      };

      return typeBasedProps[addressType];
    },
    [
      getCountry,
      formatMessage,
      getAddressType,
      addBillingAddress,
      addShippingAddress,
      setDefaultBillingAddress,
      setDefaultShippingAddress,
      updateAddress,
    ],
  );

  return { mapPropsToAddress };
};

export default usePropsToAddressType;
